import { useEffect, useState } from "react";
import { connect } from "react-redux";
import { useParams } from "react-router";
import { DebounceInput } from "react-debounce-input";
import { faUser, faEnvelope, faPhone } from "@fortawesome/free-solid-svg-icons";

import {
  emptyString,
  loginViewConstants,
  consumerResponsesConstants,
  CONSUMER_RESPONSES_LABELS,
  PERCENTAGE_FORMAT_CONSTANTS,
  LIST_SEARCH_DEBOUNCE_MS,
  SYMBOLS,
} from "../../constants";
import { getItemFromStorage, setItemToStorage } from "../../../../system";
import {
  useSpinner,
  Spinner,
  DataTable,
  paginatedPostRequestQueryBody,
  openConversationAllInternalNotesModal,
  isSuperOrBrandAdmin,
  handleAsyncAction,
} from "../../../../shared";
import {
  consumerResponsesActions,
  individualStoreProfileActions,
} from "../../redux/actions";
import useModalDisplay from "../../../../shared/components/hooks/useModalDisplay/useModalDisplay";
import { isInboxActive } from "../../../../shared/components/sidebarContainer/utility";
import { consumerResponseColumns } from "./utility";
import SubmittedSurveyComponent from "./submittedSurveyComponent";
import CustomerContactDetail from "./customerContactDetail";
import ConsumerResponseSummaryTable from "./consumerResponseSummaryTable";

const {
  CUSTOMER_RESPONSE_DATA,
  RESTAURANT_EXTERNAL_ID,
  DEFAULT_SORT_ID,
  DEFAULT_SORT_DESC,
} = consumerResponsesConstants;

const { COMPLETED_SURVEYS, SEARCH_PLACEHOLDER } = CONSUMER_RESPONSES_LABELS;

const ConsumerResponses = (props) => {
  const {
    profileData,
    systemReducer: { allBrandsWithFeatureFlags },
    consumerResponsesReducer: {
      customerUserData,
      allCustomerUserData,
      consumerResponseDataPagination,
    },
    authentication: { user },
    getStoreProfileData,
    getUserResponseByCustomerId,
  } = props;

  const { externalId } = profileData;

  const {
    surveyFilled,
    totalOrders,
    averageTransactionRating,
    averageSentimentRating,
  } = allCustomerUserData;

  const { customerId, locationId, brandId } = useParams();
  const frRoute = getItemFromStorage(loginViewConstants.FR_ROUTE);

  const { name, email, phone } = customerUserData[0] || {};

  //Initial states
  const [questionJson, setQuestionJson] = useState([]);
  const [searchString, setSearchString] = useState(emptyString);
  const [sorting, setSorting] = useState({
    id: DEFAULT_SORT_ID,
    desc: DEFAULT_SORT_DESC,
  });
  const [showModal, modalElement, setComponent, closeModal] = useModalDisplay();

  const isBrandInboxActive = isInboxActive(
    allBrandsWithFeatureFlags,
    isSuperOrBrandAdmin() ? brandId : user.brandId,
  );

  const participationRate = totalOrders
    ? (
        (surveyFilled / totalOrders) *
        PERCENTAGE_FORMAT_CONSTANTS.MULTIPLIER
      ).toFixed(PERCENTAGE_FORMAT_CONSTANTS.DECIMAL_PLACES) + SYMBOLS.PERCENTAGE
    : emptyString;

  const consumerResponseRating = [
    { id: 1, value: totalOrders },
    { id: 2, value: surveyFilled },
    { id: 3, value: participationRate },
    {
      id: 4,
      value: averageTransactionRating,
    },
    { id: 5, value: averageSentimentRating },
  ];

  //Parsing Array of responses
  const getRowData = (e) => {
    const filterOrderNumber = customerUserData.find((items) => {
      return items.externalOrderId === e.externalOrderId && items.id === e.id;
    });

    if (!filterOrderNumber?.response) {
      setQuestionJson([]);
      return;
    }

    try {
      const result = JSON.parse(filterOrderNumber.response);
      setQuestionJson(Array.isArray(result) ? result : []);
    } catch {
      setQuestionJson([]);
    }
  };

  // set pagination details with request body
  const getPaginatedPostRequestBody = (pageNumber, sortBy) => {
    const requestBody = paginatedPostRequestQueryBody(
      consumerResponseDataPagination,
      pageNumber,
      searchString,
      sortBy || sorting,
      emptyString,
    );
    if (sortBy) {
      setSorting(sortBy);
    }

    return requestBody;
  };

  // get table data with pagination
  const getData = (pageNumber, sortBy) => {
    const requestBody = getPaginatedPostRequestBody(pageNumber, sortBy);
    setItemToStorage(RESTAURANT_EXTERNAL_ID, externalId);
    getUserResponseByCustomerId(customerId, externalId, requestBody);
  };

  // Show loading while a request is running.
  const spinner = useSpinner();

  const getInternalNotesModal = (e, customerId, customerName, orderId) => {
    e.stopPropagation();
    setComponent(
      openConversationAllInternalNotesModal({
        customerId,
        closeModal,
        customerName,
        orderId,
      }),
    );
  };

  //Get user responses by its customer id before rendering the page
  useEffect(() => {
    if (externalId) {
      getData(0);
    }
    // eslint-disable-next-line
  }, [searchString, externalId]);

  //Getting Survey Profile Data
  useEffect(() => {
    handleAsyncAction(
      frRoute
        ? getStoreProfileData(locationId, true)
        : getStoreProfileData(locationId, false),
    );
    // eslint-disable-next-line
  }, []);

  return (
    <>
      {showModal && modalElement}
      {spinner(CUSTOMER_RESPONSE_DATA) ? (
        <Spinner />
      ) : (
        <div className="container-fluid py-3">
          <div className="bg-transparent">
            <div className="bg-white py-3 shadow rounded">
              <div className="px-4">
                <div className="d-flex flex-column flex-md-row align-items-md-center gap-2 gap-md-4">
                  <CustomerContactDetail icon={faUser} value={name} />

                  {isBrandInboxActive ? (
                    <>
                      <CustomerContactDetail
                        icon={faEnvelope}
                        value={email}
                        hideWhenEmpty
                      />
                      <CustomerContactDetail
                        icon={faPhone}
                        value={phone}
                        hideWhenEmpty
                      />
                    </>
                  ) : null}
                </div>
                <hr />
                <ConsumerResponseSummaryTable metrics={consumerResponseRating} />
              </div>
            </div>
            <div className="mt-3 bg-white shadow rounded">
              <div className="d-flex justify-content-between ">
                <h5 className="px-4 pt-4">{COMPLETED_SURVEYS}</h5>
                <div className="mx-4 mb-2 pt-3 search-container w20">
                  <DebounceInput
                    debounceTimeout={LIST_SEARCH_DEBOUNCE_MS}
                    type="text"
                    className="search-restaurants"
                    placeholder={SEARCH_PLACEHOLDER}
                    value={searchString}
                    onChange={(e) => {
                      setQuestionJson([]);
                      setSearchString(e.target.value);
                    }}
                  />
                  <i className="fa fa-search search-icon" aria-hidden="true" />
                </div>
              </div>
              <hr className="mx-4" />
              <DataTable
                columns={consumerResponseColumns(
                  isBrandInboxActive,
                  getInternalNotesModal,
                  customerId,
                  customerUserData[0]?.name,
                  locationId,
                  brandId,
                )}
                data={customerUserData}
                sortedBy={sorting}
                onRowClick={(e) => getRowData(e)}
                isRowHighlight={true}
                totalRows={consumerResponseDataPagination.totalElements}
                numberOfRowsPerPage={consumerResponseDataPagination.size}
                pageNumber={consumerResponseDataPagination.number + 1}
                onPageChange={(pageNum) => getData(pageNum)}
                onColumnSort={(sortBy) => {
                  setQuestionJson([]);
                  getData(consumerResponseDataPagination.number, sortBy);
                }}
                isPaginationAfterFiveRecords
              />
            </div>
          </div>
          {/* Create Condition For Questions */}
          <SubmittedSurveyComponent
            questionJson={questionJson}
            key={
              questionJson[0]?.SurveyQuestionAnswerBaseId || questionJson[0]?.Id
            }
          />
        </div>
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  authentication: state.authentication,
  systemReducer: state.system,
  profileData: state.individualStoreProfileReducer.profileData,
  consumerResponsesReducer: state.sharedConsumerResponsesReducer,
});

const mapDispatchToProps = (dispatch) => ({
  getUserResponseByCustomerId: (customerId, externalId, body) =>
    dispatch(
      consumerResponsesActions.getUserResponseByCustomerId(
        customerId,
        externalId,
        body,
      ),
    ),
  getStoreProfileData: (locationId, fromFR) =>
    dispatch(
      individualStoreProfileActions.getStoreProfileData(locationId, fromFR),
    ),
});

export default connect(mapStateToProps, mapDispatchToProps)(ConsumerResponses);
